import { CheckIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Flex,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InsurancePlan } from '../../types/plans/types';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedPlanIds: string[]) => void;
  plans: InsurancePlan[];
  selectedRows: Set<string>;
  context?: {
    scopeType: string;
    scopeId: string;
    activeFilters: Record<string, unknown>;
  };
}

const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  plans,
  selectedRows,
  context,
}) => {
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);

  // Extract all unique insurance plans from the data
  const availablePlans = useMemo(() => {
    const planMap = new Map<string, { id: string; name: string; count: number }>();

    plans.forEach((row) => {
      row.plans.forEach((plan) => {
        const insurance = plan.insurance;
        const key = insurance.id;
        if (!planMap.has(key)) {
          planMap.set(key, {
            id: insurance.id,
            name: insurance.friendlyName || insurance.companyName || insurance.emrInsurancePlan || 'Unknown Plan',
            count: 0,
          });
        }
        planMap.get(key)!.count++;
      });
    });

    return Array.from(planMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [plans]);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPlanIds([]);
    }
  }, [isOpen]);

  const handlePlanToggle = useCallback((planId: string) => {
    setSelectedPlanIds(prev =>
      prev.includes(planId)
        ? prev.filter(id => id !== planId)
        : [...prev, planId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedPlanIds(availablePlans.map(plan => plan.id));
  }, [availablePlans]);

  const handleDeselectAll = useCallback(() => {
    setSelectedPlanIds([]);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(selectedPlanIds);
    onClose();
  }, [selectedPlanIds, onConfirm, onClose]);

  const selectedRowsCount = selectedRows.size;
  const selectedPlansCount = selectedPlanIds.length;

  // Generate context summary
  const contextSummary = useMemo(() => {
    const parts = [];
    if (context?.activeFilters?.marketId) parts.push(`Market: ${context.activeFilters.marketId}`);
    if (context?.activeFilters?.state) parts.push(`State: ${context.activeFilters.state}`);
    if (context?.activeFilters?.search) parts.push(`Search: "${context.activeFilters.search}"`);
    return parts.length > 0 ? parts.join(' • ') : 'All available data';
  }, [context]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="lg" fontWeight="600">
              Bulk Edit Insurance Plans
            </Text>
            <Text fontSize="sm" color="gray.600">
              Step 1: Select which insurance plans to edit
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {/* Summary */}
            <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
              <VStack align="stretch" spacing={2}>
                <Text fontSize="sm" fontWeight="600" color="blue.800">
                  Edit Summary
                </Text>
                <Text fontSize="sm" color="blue.700">
                  • <strong>{selectedRowsCount}</strong> provider/practice combinations selected
                </Text>
                <Text fontSize="sm" color="blue.700">
                  • Filtered by: <strong>{contextSummary}</strong>
                </Text>
                <Text fontSize="sm" color="blue.700">
                  • <strong>{availablePlans.length}</strong> insurance plans available
                </Text>
              </VStack>
            </Box>

            {/* Plan Selection Controls */}
            <Flex justify="space-between" align="center">
              <Text fontSize="sm" fontWeight="600">
                Select Insurance Plans to Edit:
              </Text>
              <HStack spacing={2}>
                <Button size="xs" onClick={handleSelectAll} variant="outline">
                  Select All ({availablePlans.length})
                </Button>
                <Button size="xs" onClick={handleDeselectAll} variant="outline">
                  Deselect All
                </Button>
              </HStack>
            </Flex>

            {/* Plan Selection List */}
            <Box maxH="300px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
              <Stack spacing={0} p={2}>
                {availablePlans.map((plan) => {
                  const isSelected = selectedPlanIds.includes(plan.id);
                  return (
                    <Flex
                      key={plan.id}
                      align="center"
                      p={3}
                      borderRadius="md"
                      _hover={{ bg: 'gray.50' }}
                      cursor="pointer"
                      onClick={() => handlePlanToggle(plan.id)}
                    >
                      <Checkbox
                        isChecked={isSelected}
                        onChange={() => handlePlanToggle(plan.id)}
                        mr={3}
                        colorScheme="blue"
                      />
                      <VStack align="stretch" flex={1} spacing={1}>
                        <Text fontSize="sm" fontWeight={isSelected ? "600" : "500"}>
                          {plan.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Affects {plan.count} provider/practice combinations
                        </Text>
                      </VStack>
                      {isSelected && (
                        <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                          Selected
                        </Badge>
                      )}
                    </Flex>
                  );
                })}
              </Stack>
            </Box>

            {/* Selection Summary */}
            {selectedPlansCount > 0 && (
              <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                <HStack spacing={2}>
                  <CheckIcon color="green.600" boxSize={4} />
                  <Text fontSize="sm" color="green.700">
                    <strong>{selectedPlansCount}</strong> insurance plan{selectedPlansCount !== 1 ? 's' : ''} selected
                  </Text>
                </HStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirm}
              isDisabled={selectedPlansCount === 0}
              leftIcon={<CheckIcon />}
            >
              Continue to Bulk Edit ({selectedPlansCount} plans)
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkEditModal;
